import { useState, useEffect, useMemo } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';
import { formatEuros } from '../lib/format.js';

export default function SubscriptionFormModal({ isOpen, debtors, products, onClose, onSaved }) {
  const [debtorId, setDebtorId] = useState('');
  const [productId, setProductId] = useState('');
  const [planId, setPlanId] = useState('');
  const [billingInterval, setBillingInterval] = useState('year');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const sellable = useMemo(() => products.filter(p => p.isSellable), [products]);
  const activeDebtors = useMemo(() => debtors.filter(d => d.status === 'active'), [debtors]);
  const chosenProduct = sellable.find(p => String(p.id) === String(productId)) || null;
  const plans = chosenProduct ? chosenProduct.plans.filter(p => p.isSellable) : [];
  const chosenPlan = plans.find(p => String(p.id) === String(planId)) || null;

  // Why: alleen `isOpen` in de deps. `sellable` is een nieuwe array zodra de
  // lijst op de achtergrond ververst — die meenemen zou het formulier onder de
  // handen van de gebruiker leegmaken terwijl hij aan het invullen is.
  useEffect(() => {
    if (!isOpen) return;
    const only = products.filter(p => p.isSellable);
    setDebtorId('');
    setProductId(only.length === 1 ? String(only[0].id) : '');
    setPlanId('');
    setBillingInterval('year');
    setError(null);
  }, [isOpen]);

  // Why: bij het wisselen van product hoort een pakket van het vorige product
  // niet geselecteerd te blijven staan. De server zou het weigeren, maar de
  // gebruiker hoort het al eerder te merken.
  function changeProduct(value) {
    setError(null);
    setProductId(value);
    setPlanId('');
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          debtor_id: Number(debtorId),
          plan_id: Number(planId),
          billing_interval: billingInterval
        })
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || 'save_failed');
      onSaved(data);
      onClose();
    } catch (err) {
      setError(translate(err.message));
    } finally {
      setBusy(false);
    }
  }

  const priceCents = chosenPlan
    ? (billingInterval === 'year' ? chosenPlan.yearlyPriceCents : chosenPlan.monthlyPriceCents)
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nieuw abonnement">
      <form onSubmit={submit} class="app-form">
        <label>
          <span>Debiteur</span>
          <select value={debtorId} onChange={e => setDebtorId(e.currentTarget.value)} required>
            <option value="">— kies een debiteur —</option>
            {activeDebtors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        {activeDebtors.length === 0 && (
          <p class="muted small">Nog geen actieve debiteuren. Maak er eerst één aan of keur een aanvraag goed.</p>
        )}

        <label>
          <span>Product</span>
          <select value={productId} onChange={e => changeProduct(e.currentTarget.value)} required>
            <option value="">— kies een product —</option>
            {sellable.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {sellable.length === 0 && (
          <p class="muted small">
            Nog geen verkoopbaar product. Ga naar <strong>Producten</strong> en stel
            via de knop <strong>Pakketten</strong> minstens één pakket met prijzen in.
          </p>
        )}

        <label>
          <span>Pakket</span>
          <select
            value={planId}
            onChange={e => { setError(null); setPlanId(e.currentTarget.value); }}
            required
            disabled={!chosenProduct}
          >
            <option value="">— kies een pakket —</option>
            {plans.map(plan => (
              <option key={plan.id} value={plan.id}>
                {plan.name} — {plan.maxUsers == null ? 'onbeperkt' : `t/m ${plan.maxUsers} gebruikers`}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Facturatie</span>
          <select value={billingInterval} onChange={e => setBillingInterval(e.currentTarget.value)}>
            <option value="year">Per jaar</option>
            <option value="month">Per maand</option>
          </select>
        </label>

        {priceCents != null && (
          <p class="muted small">
            Bedrag: <strong>{formatEuros(priceCents)}</strong> excl. BTW,
            {billingInterval === 'year' ? ' één factuur per jaar.' : ' twaalf facturen per jaar.'}
          </p>
        )}

        {error && <div class="form-error">{error}</div>}
        <div class="form-actions">
          <Button variant="ghost" type="button" onClick={onClose}>Annuleren</Button>
          <Button type="submit" disabled={busy || !debtorId || !planId}>
            {busy ? 'Bezig…' : 'Aanmaken'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function translate(code) {
  const messages = {
    invalid_input: 'Kies een debiteur, een pakket en een facturatieperiode.',
    debtor_not_found: 'Deze debiteur bestaat niet meer — herlaad de pagina.',
    plan_not_sellable: 'Dit pakket staat niet als verkoopbaar ingesteld.',
    plan_missing_price: 'Dit pakket heeft nog geen prijs voor de gekozen periode.',
    subscription_exists: 'Deze debiteur heeft dit product al lopen.',
    save_failed: 'Opslaan is mislukt.'
  };
  return messages[code] || 'Er ging iets mis. Probeer opnieuw.';
}
